import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import { AppBar, Button, ButtonBase, IconButton, Skeleton, Stack, Toolbar, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import { GlobalSearch } from '@/components/common/GlobalSearch'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useOrganization } from '@/features/organization/hooks'
import { NotificationsBell } from '@/features/notifications/NotificationsBell'
import { useActiveTerritory } from '@/hooks/useActiveTerritory'
import { useSystemHealth } from '@/hooks/useSystemHealth'
import { paths } from '@/routes/paths'
import { setOperatorClientView } from '@/routes/platformView'
import { UserMenu } from '@/layouts/components/UserMenu'
import { color, StatusIndicator } from '@/v2/design-system'
import { navItemsV2 } from '@/v2/layouts/navigation'

function useActiveScreenLabel(): string {
  const { pathname } = useLocation()
  const match = navItemsV2.find((item) =>
    item.path === '/v2' ? pathname === '/v2' : pathname.startsWith(item.path),
  )
  return match?.label ?? 'GOVIA'
}

/**
 * Franja superior contextual — responde "¿dónde estoy y en qué contexto
 * trabajo?". REUTILIZA `useActiveTerritory()` (sin query territorial
 * nueva), `useSystemHealth()` (misma queryKey que el resto de GOVIA, sin
 * llamada duplicada), `NotificationsBell` y `UserMenu` tal cual — cero
 * lógica nueva salvo lo indicado abajo. Nunca expone backend/environment/
 * OpenAI/Celery/Redis/costos — ver "Client vs Super Admin" en el encargo
 * UX-2.1.
 *
 * UX-2.1 — agrega `useOrganization()` (nombre real de la organización
 * activa, mismo caché que `OrganizationPage`) para satisfacer "Active
 * organization/territory context" del Product Shell. Deliberadamente NO
 * se agrega un selector de periodo de análisis ("Date/time... where
 * supported"): no existe hoy ningún estado de periodo/rango compartido
 * en la aplicación (cada pantalla que filtra por fecha lo hace con su
 * propio estado local) — inventar un selector global aquí sin una fuente
 * real que lo consuma sería fabricar contexto, no exponerlo. Queda
 * documentado como diferido, no como omisión silenciosa (ver reporte
 * final UX-2.1, hallazgos).
 */
export function TopContextBar({ onMenuClick }: { onMenuClick: () => void }) {
  const screenLabel = useActiveScreenLabel()
  const { user } = useAuth()
  const { territory, isLoading: territoryLoading } = useActiveTerritory()
  const orgQuery = useOrganization()
  const healthQuery = useSystemHealth()
  const monitoringActive = healthQuery.data?.status === 'ok'
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <AppBar position="sticky" elevation={0} color="transparent">
      <Toolbar sx={{ gap: 1.5, minHeight: '64px !important', py: 1 }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          aria-label="Abrir navegación"
        >
          <MenuRoundedIcon />
        </IconButton>

        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {screenLabel}
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            divider={<Stack sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            {orgQuery.isLoading ? (
              <Skeleton variant="text" width={100} />
            ) : orgQuery.data?.name ? (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                {orgQuery.data.name}
              </Typography>
            ) : null}
            {territoryLoading ? (
              <Skeleton variant="text" width={90} />
            ) : territory ? (
              <Typography variant="caption" color="text.secondary">
                {territory.label}
              </Typography>
            ) : null}
            <StatusIndicator
              tone={monitoringActive ? 'success' : 'normal'}
              label={monitoringActive ? 'Monitoreo activo' : 'Monitoreo interrumpido'}
            />
          </Stack>
        </Stack>

        {/* UX-9 RESPONSIVE / QA CLOSURE: el botón de texto completo competía
            por ancho con el bloque de título+organización+territorio,
            forzando "Buscar en GOVIA…" a partirse en varias líneas dentro
            de un contenedor demasiado angosto — hallazgo ya conocido de
            Fase B a 768px. Verificado con captura real: el mismo problema
            (más leve, 2 líneas) reaparece a 1024px (`md`), un ancho
            "intermedio" donde el botón de texto sigue sin tener espacio
            real. Cambio mínimo: el corte de breakpoint pasa de `sm`
            (600px) a `lg` (1200px), así 768px Y 1024px usan el disparador
            solo-icono que YA existía (antes reservado para <600px) — sin
            perder funcionalidad, sin texto que envuelva. Solo 1440px
            (>= lg) sigue mostrando el botón de texto completo, donde ya
            cabía sin problema (verificado, captura real). */}
        <ButtonBase
          onClick={() => setSearchOpen(true)}
          sx={{
            flex: 1,
            maxWidth: 420,
            ml: { xs: 0, lg: 2 },
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            border: `1px solid ${color.border}`,
            color: 'text.secondary',
            justifyContent: 'flex-start',
            gap: 1,
            display: { xs: 'none', lg: 'flex' },
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
            Buscar en GOVIA…
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Ctrl+K
          </Typography>
        </ButtonBase>

        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 'auto' }}>
          <IconButton
            onClick={() => setSearchOpen(true)}
            aria-label="Buscar"
            sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
          >
            <SearchRoundedIcon />
          </IconButton>
          {/* Solo operadores de plataforma: regreso explícito a la consola
              (los usuarios cliente nunca ven este botón). */}
          {user?.is_platform_operator && (
            <Button
              size="small"
              variant="outlined"
              component={RouterLink}
              to={paths.superAdmin}
              onClick={() => setOperatorClientView(false)}
              sx={{ mr: 1, whiteSpace: 'nowrap' }}
            >
              Consola de plataforma
            </Button>
          )}
          <NotificationsBell />
          <UserMenu />
        </Stack>
      </Toolbar>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} v2 />
    </AppBar>
  )
}
